import LocalImages from "assets/images/local-images";
import Content, { ContentNS } from "content/content";
import LinkButton from "features/shared/components/buttons/link-button/LinkButton";
import useMultiPaxSession from "features/shared/hooks/useMultiPaxSession";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import { MicroHeader } from "features/shared/styles/typography/MicroHeader.styled";
import { Summary } from "features/shared/styles/typography/Summary.styled";
import ChecklistResolutionStatus from "models/checklist/checklist-resolution-status";
import Pax from "models/pax";
import { FC } from "react";
import { useAppStore } from "store/store";
import { normalizeName } from "utils";

import PaxDetails from "./styles/PaxDetails.styled";
import PaxInfoContainer from "./styles/PaxInfoContainer.styled";
import { getSessionRelativeUrl } from "features/shared/hooks/navigation/useFlowRedirect";
import { PaxCheckRequiredAction } from "api";

interface PaxProps {
  requiredAction: PaxCheckRequiredAction | undefined;
  status: ChecklistResolutionStatus;
  passenger: Pax | null;
}

const PassengerInfo: FC<PaxProps> = ({ passenger, status, requiredAction }) => {
  const navigate = useNavigateWithQuery();
  const sessionId = useAppStore((state) => state.sessionId);
  const isMultiPax = useMultiPaxSession();

  if (passenger === null) {
    return null;
  }

  const images = LocalImages.shared.images;
  const text = (s: string) => Content.shared.t(s, { ns: ContentNS.checklist });
  const textUI = (s: string) => Content.shared.t(s, { ns: ContentNS.ui });

  const paxName = normalizeName(`${passenger.firstName} ${passenger.surname}`);

  const onSeeAllPaxClick = () => {
    navigate(`${getSessionRelativeUrl(sessionId)}/select-passenger`);
  };

  const statusToPresent =
    requiredAction === undefined
      ? status
      : ChecklistResolutionStatus.ActionRequired;

  return (
    <PaxInfoContainer resolutionStatus={statusToPresent}>
      <img alt="icon of checklist status" src={statusIcon()} />
      <PaxDetails resolutionStatus={statusToPresent}>
        <Summary>{paxName}</Summary>
        <MicroHeader>
          {text(`resolution-status.${statusToPresent}`)}
        </MicroHeader>
        {isMultiPax ? (
          <LinkButton onClick={onSeeAllPaxClick}>
            {textUI("see-all-passengers")}
          </LinkButton>
        ) : null}
      </PaxDetails>
    </PaxInfoContainer>
  );

  function statusIcon(): string {
    switch (statusToPresent) {
      case ChecklistResolutionStatus.ReadyToFly:
        return images.icons.session.resolved;
      case ChecklistResolutionStatus.ActionRequired:
        return images.icons.session.actionRequired;
      case ChecklistResolutionStatus.EntryNotAllowed:
        return images.icons.session.hasIssues;
      default:
        return images.icons.session.actionRequired;
    }
  }
};

export default PassengerInfo;
